import {
    Filter as SpaceFilter,
    Projection as SpaceProjection,
    Sort as SpaceSort,
    TimeFilter as SpaceTimeFilter,
    TimeRange as SpaceTimeRange,
    WidgetRequestGroupBy as SpaceGroupBy,
} from '@sprinklr/modules/research/widget/types/index';

import {
    AnalyticsEngine,
    AnalyticsFilter,
    AnalyticsGroupBy,
    AnalyticsProjection,
    AnalyticsRequest,
    AnalyticsSort,
    AnalyticsTimePeriod,
    TimePeriodKey,
} from '@sprinklr/stories/analytics/AnalyticsRequest';
import { SpaceRequest } from '@sprinklr/stories/reporting/types';

export const getDeepestGroupBy = (groupBys?: SpaceGroupBy[]): SpaceGroupBy | undefined => {
    if (groupBys && groupBys.length > 0) {
        const firstGroupBy = groupBys[0];
        if (firstGroupBy.childrenGroupBys && firstGroupBy.childrenGroupBys.length > 0) {
            return getDeepestGroupBy(firstGroupBy.childrenGroupBys);
        }
        return firstGroupBy;
    }
    return undefined;
};

export const getAllProjections = (groupBys?: SpaceGroupBy[]): SpaceProjection[] => {
    let allProjections = [];
    if (!groupBys || groupBys.length === 0) {
        return allProjections;
    }

    allProjections = [];
    groupBys.map(groupBy => {
        allProjections = [
            ...allProjections,
            ...(groupBy.projections || []),
            ...getAllProjections(groupBy.childrenGroupBys),
        ];
    });

    return allProjections;
};

export const getAllGroupBys = (groupBys?: SpaceGroupBy[]): SpaceGroupBy[] => {
    let allGroupBys = [];
    if (!groupBys || groupBys.length === 0) {
        return allGroupBys;
    }
    allGroupBys = [...groupBys];
    groupBys.map(groupBy => {
        allGroupBys = [...allGroupBys, ...getAllGroupBys(groupBy.childrenGroupBys)];
    });

    return allGroupBys;
};

export const toAnalyticsProjection = (spaceProjection: SpaceProjection): AnalyticsProjection => {
    const measurement = spaceProjection.measurement.replace('Copy of ', ''); //remove "Copy Of" from measurementName so that request doesn't error out
    return {
        aggregateFunction: spaceProjection.aggregateFunction as any,
        measurementName: measurement,
        heading: spaceProjection.key || measurement, // TODO: how do we calculate heading?
        details: spaceProjection.details,
    };
};

export const toAnalyticsGroupBy = (spaceGroupBy: SpaceGroupBy): AnalyticsGroupBy => {
    const groupBy: AnalyticsGroupBy = {
        dimensionName: spaceGroupBy.field,
        details: spaceGroupBy.details,
        groupType: spaceGroupBy.groupType,
        heading: spaceGroupBy.key || spaceGroupBy.field, // TODO: how do we calculate heading?
        field: undefined, // TODO
    };
    if (spaceGroupBy.page) {
        groupBy.limit = spaceGroupBy.page.size;
        groupBy.limitType = 'TOP';
    }
    return groupBy;
};

export const toAnalyticsFilter = (spaceFilter: SpaceFilter): AnalyticsFilter => {
    return {
        dimensionName: spaceFilter.field,
        filterType: spaceFilter.filterType,
        values: spaceFilter.values,
        field: undefined, // TODO
        details: spaceFilter.details,
        allValues: spaceFilter.allValuesAllowed,
    };
};

export const toAnalyticsSort = (spaceSort: SpaceSort): AnalyticsSort => {
    return {
        heading: spaceSort.key,
        order: spaceSort.order,
        details: spaceSort.additional,
        isDimension: undefined,
    };
};

export const toAnalyticsTimeRange = (spaceTimeRange: SpaceTimeRange | 'CUSTOM'): TimePeriodKey => {
    switch (spaceTimeRange) {
        case 'CUSTOM':
        case 'TODAY':
        case 'YESTERDAY':
        case 'LAST_12_HOURS':
        case 'LAST_24_HOURS':
        case 'THIS_WEEK':
        case 'LAST_WEEK':
        case 'LAST_7_DAYS':
        case 'THIS_MONTH':
        case 'LAST_MONTH':
        case 'LAST_28_DAYS':
        case 'LAST_30_DAYS':
        case 'LAST_60_DAYS':
        case 'LAST_90_DAYS':
        case 'LAST_120_DAYS':
        case 'THIS_YEAR':
        case 'LAST_YEAR':
        case 'LAST_365_DAYS':
        default:
            if (typeof spaceTimeRange !== 'string' || Array.isArray(spaceTimeRange)) {
                throw new Error(`${JSON.stringify(spaceTimeRange)} is not a valid time range`);
            }
            return spaceTimeRange.toLowerCase() as TimePeriodKey;
        case 'LAST_1_HOUR':
            return 'last_x_hours';
        case 'LAST_180_DAYS':
            return 'last_90_days';
        case 'LIFETIME':
            return 'all_time';
    }
};

export const toAnalyticsTimePeriod = (
    spaceTimeFilter: SpaceTimeFilter,
    timeZone?: string
): AnalyticsTimePeriod => {
    return {
        key: spaceTimeFilter.timeRange
            ? toAnalyticsTimeRange(spaceTimeFilter.timeRange)
            : undefined,
        endTime:
            spaceTimeFilter.timeRange === 'CUSTOM' || !spaceTimeFilter.timeRange
                ? spaceTimeFilter.untilTime
                : undefined,
        startTime:
            spaceTimeFilter.timeRange === 'CUSTOM' || !spaceTimeFilter.timeRange
                ? spaceTimeFilter.sinceTime
                : undefined,
        timeZone,
    };
};

const toAnalyticsRequest = (spaceRequest: SpaceRequest): AnalyticsRequest => {
    const deepestGroupBy = getDeepestGroupBy(spaceRequest.groupBys);
    const allGroupBys = getAllGroupBys(spaceRequest.groupBys);
    const allProjections =
        allGroupBys.length > 0
            ? getAllProjections(spaceRequest.groupBys)
            : spaceRequest.projections || [];
    const timePeriods: AnalyticsTimePeriod[] = [];
    if (spaceRequest.timeFilter) {
        timePeriods.push(toAnalyticsTimePeriod(spaceRequest.timeFilter, spaceRequest.timezone));
    }
    if (spaceRequest.previousTimeFilter) {
        // TODO: implement previous time period
    }

    const sorts = spaceRequest.sorts ? spaceRequest.sorts.map(toAnalyticsSort) : [];
    for (const sort of sorts) {
        sort.isDimension = !!allGroupBys.find(groupBy => groupBy.key === sort.heading);
    }

    const analyticsRequest: Partial<AnalyticsRequest> = {
        reportingEngine: spaceRequest.reportingEngine as AnalyticsEngine,
        report: spaceRequest.report,
        projections: allProjections.map(toAnalyticsProjection),
        includeTotal: allGroupBys.length > 0 && spaceRequest.projections?.length > 0,
        projectionDecorations: spaceRequest.projectionDecorations,
        groupBys: allGroupBys.map(toAnalyticsGroupBy),
        filters: spaceRequest.filters ? spaceRequest.filters.map(toAnalyticsFilter) : [],
        sorts,
        additional: spaceRequest.additional,
        timePeriods,
        timeField: spaceRequest.reportDateField,
    };

    if (spaceRequest.page && typeof spaceRequest.page.size === 'number') {
        analyticsRequest.limit = spaceRequest.page.size;
    }

    return analyticsRequest as AnalyticsRequest;
};

export default toAnalyticsRequest;
